import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { SESSIONS } from "@/lib/sessions";
import { store } from "@/lib/storage";
import SessionIllustration from "@/components/SessionIllustration";
import { Heart, Play, TrendingDown, Minus, Sparkles, Crown } from "lucide-react";

// Séances gratuites (miroir de FREE_SESSION_IDS côté backend)
const FREE_SESSIONS = new Set(["respiration-5", "present-5", "nuit-20", "sport-5"]);

function formatDate(iso) {
  try {
    const d = new Date(iso);
    return d.toLocaleString("fr-FR", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });
  } catch { return ""; }
}

const MONTH_NAMES = ["janvier","février","mars","avril","mai","juin","juillet","août","septembre","octobre","novembre","décembre"];

function computeBilan(history) {
  const now = new Date();
  const yyyy = now.getFullYear();
  const mm = now.getMonth();
  const monthEntries = history.filter(h => {
    const d = new Date(h.at);
    return d.getFullYear() === yyyy && d.getMonth() === mm;
  });
  const withScores = monthEntries.filter(h => typeof h.preScore === "number" && typeof h.postScore === "number");
  const completedCount = monthEntries.filter(h => h.completed).length;
  const avgPre = withScores.length ? withScores.reduce((s, h) => s + h.preScore, 0) / withScores.length : null;
  const avgPost = withScores.length ? withScores.reduce((s, h) => s + h.postScore, 0) / withScores.length : null;
  const totalMinutes = monthEntries.filter(h => h.completed).reduce((s, h) => s + (h.durationDone || h.minutes || 0), 0);
  // Nouveau : compte des séances Premium (payantes) écoutées ce mois-ci
  const premiumEntries = monthEntries.filter(h => h.id && !FREE_SESSIONS.has(h.id));
  const premiumTitles = Array.from(new Set(premiumEntries.map(h => h.title))).slice(0, 3);
  return {
    label: `${MONTH_NAMES[mm]} ${yyyy}`,
    sessions: monthEntries.length,
    completed: completedCount,
    totalMinutes,
    avgPre, avgPost,
    scoredCount: withScores.length,
    premiumCount: premiumEntries.length,
    premiumTitles,
  };
}

export default function Favorites() {
  const [favs, setFavs] = useState([]);
  const [history, setHistory] = useState([]);

  useEffect(() => {
    setFavs(store.getFavorites());
    setHistory(store.getHistory());
  }, []);

  const favSessions = SESSIONS.filter(s => favs.includes(s.id));
  const bilan = useMemo(() => computeBilan(history), [history]);

  const delta = bilan.avgPre != null && bilan.avgPost != null ? (bilan.avgPre - bilan.avgPost) : null;

  return (
    <div className="animate-fade-in pt-6">
      <h1 className="font-serif text-3xl text-[color:var(--ivoire)]">Favoris & Historique</h1>
      <p className="text-sm text-[color:var(--ivoire-muted)] mt-2">Retrouvés sur ce compte si vous êtes connecté, sinon conservés sur cet appareil.</p>

      {/* Bilan doux */}
      <section className="mt-8" data-testid="bilan-section">
        <div className="flex items-center gap-2 mb-3">
          <Sparkles size={14} className="text-[color:var(--sauge-light)]" />
          <p className="font-mono text-[10px] tracking-widest uppercase text-[color:var(--sauge-light)]">Bilan doux · {bilan.label}</p>
        </div>

        <div className="rounded-2xl border border-[color:var(--border)] bg-gradient-to-br from-[#142235]/80 to-[#0D1B2A]/60 p-5">
          {bilan.sessions === 0 ? (
            <p className="text-sm text-[color:var(--ivoire-muted)] leading-relaxed">
              Aucune séance ce mois-ci pour l'instant. Prenez le temps qui vous convient.
            </p>
          ) : (
            <>
              <div className="grid grid-cols-2 gap-3">
                <StatCard testid="bilan-sessions" label="Séances" value={String(bilan.sessions)} sub={bilan.completed <= 1 ? `${bilan.completed} terminée` : `${bilan.completed} terminées`} />
                <StatCard testid="bilan-minutes" label="Temps calme" value={`${bilan.totalMinutes} min`} sub="cumulé ce mois" />
              </div>

              {bilan.premiumCount > 0 && (
                <div className="mt-3 rounded-xl bg-[color:var(--sauge)]/10 border border-[color:var(--sauge)]/30 p-4" data-testid="bilan-premium">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Crown size={14} className="text-[color:var(--sauge-light)]" />
                      <span className="font-mono text-[10px] uppercase tracking-widest text-[color:var(--sauge-light)]">
                        Séances Premium
                      </span>
                    </div>
                    <span className="font-serif text-2xl text-[color:var(--ivoire)]">{bilan.premiumCount}</span>
                  </div>
                  {bilan.premiumTitles.length > 0 && (
                    <p className="text-[11px] text-[color:var(--ivoire-muted)] mt-2 line-clamp-2">
                      {bilan.premiumTitles.join(" · ")}
                    </p>
                  )}
                </div>
              )}

              {bilan.scoredCount > 0 ? (
                <div className="mt-4 rounded-xl bg-[color:var(--bg-deep)]/50 p-4" data-testid="bilan-scores">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-[color:var(--ivoire-muted)]">Ressenti moyen</span>
                    <span className="font-mono text-xs text-[color:var(--ivoire-subtle)]">{bilan.scoredCount} séance{bilan.scoredCount > 1 ? "s" : ""} notée{bilan.scoredCount > 1 ? "s" : ""}</span>
                  </div>
                  <div className="mt-3 flex items-center gap-4">
                    <ScoreBubble label="Avant" value={bilan.avgPre} />
                    <div className="flex-1 flex items-center gap-2">
                      <div className="h-px flex-1 bg-[color:var(--border)]" />
                      {delta > 0.3 ? (
                        <span className="flex items-center gap-1 text-[color:var(--sauge-light)] text-xs" data-testid="bilan-delta">
                          <TrendingDown size={14} /> −{delta.toFixed(1)}
                        </span>
                      ) : (
                        <span className="flex items-center gap-1 text-[color:var(--ivoire-muted)] text-xs" data-testid="bilan-delta">
                          <Minus size={14} /> stable
                        </span>
                      )}
                      <div className="h-px flex-1 bg-[color:var(--border)]" />
                    </div>
                    <ScoreBubble label="Après" value={bilan.avgPost} highlight />
                  </div>
                  <p className="mt-4 text-[11px] text-[color:var(--ivoire-subtle)] leading-relaxed">
                    Observation personnelle. Pas une mesure clinique. Aucun jugement, aucune pression.
                  </p>
                </div>
              ) : (
                <p className="mt-4 text-xs text-[color:var(--ivoire-muted)] leading-relaxed">
                  Notez votre ressenti avant et après une séance pour voir apparaître votre bilan.
                </p>
              )}
            </>
          )}
        </div>
      </section>

      <section className="mt-8" data-testid="favorites-section">
        <div className="flex items-center gap-2 mb-3">
          <Heart size={14} className="text-[color:var(--sauge-light)]" />
          <p className="font-mono text-[10px] tracking-widest uppercase text-[color:var(--sauge-light)]">Favoris</p>
        </div>
        {favSessions.length === 0 ? (
          <p className="text-sm text-[color:var(--ivoire-muted)]">Aucun favori pour l'instant.</p>
        ) : (
          <div className="space-y-2">
            {favSessions.map(s => (
              <Link
                key={s.id}
                to={`/lecteur/${s.id}`}
                data-testid={`fav-item-${s.id}`}
                className="flex items-center gap-3 rounded-xl border border-[color:var(--border)] bg-[#142235]/70 p-3 hover:bg-[#1B2A3E]"
              >
                <div className="w-12 h-12 rounded-xl bg-[color:var(--bg-deep)]/60 flex items-center justify-center flex-shrink-0">
                  <SessionIllustration ambient={s.ambient} method={s.method} size={44} />
                </div>
                <div className="flex-1">
                  <div className="text-[color:var(--ivoire)] font-medium text-sm">{s.title}</div>
                  <div className="text-xs text-[color:var(--ivoire-muted)]">{s.minutes} min</div>
                </div>
                <Play size={14} className="text-[color:var(--sauge-light)]" fill="currentColor" />
              </Link>
            ))}
          </div>
        )}
      </section>

      <section className="mt-10" data-testid="history-section">
        <p className="font-mono text-[10px] tracking-widest uppercase text-[color:var(--sauge-light)] mb-3">Historique</p>
        {history.length === 0 ? (
          <p className="text-sm text-[color:var(--ivoire-muted)]">Aucune séance terminée pour l'instant.</p>
        ) : (
          <div className="space-y-2">
            {history.slice(0, 20).map((h, i) => (
              <div key={`${h.at}-${h.id ?? h.title}`} data-testid={`history-item-${i}`} className="rounded-xl border border-[color:var(--border)] bg-[#142235]/50 p-3">
                <div className="flex items-center justify-between">
                  <div className="text-[color:var(--ivoire)] text-sm">{h.title}</div>
                  <div className="text-[11px] text-[color:var(--ivoire-muted)] font-mono">{formatDate(h.at)}</div>
                </div>
                <div className="text-[11px] text-[color:var(--ivoire-muted)] mt-1 flex items-center gap-2">
                  <span>{h.minutes} min · {h.completed ? "complétée" : "interrompue"}</span>
                  {typeof h.preScore === "number" && typeof h.postScore === "number" && (
                    <span className="ml-auto font-mono">
                      {h.preScore} → {h.postScore}
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}

function StatCard({ testid, label, value, sub }) {
  return (
    <div data-testid={testid} className="rounded-xl bg-[color:var(--bg-deep)]/50 p-3">
      <div className="text-[10px] font-mono uppercase tracking-widest text-[color:var(--ivoire-subtle)]">{label}</div>
      <div className="font-serif text-2xl text-[color:var(--ivoire)] mt-0.5">{value}</div>
      <div className="text-[11px] text-[color:var(--ivoire-muted)]">{sub}</div>
    </div>
  );
}

function ScoreBubble({ label, value, highlight }) {
  return (
    <div className="text-center">
      <div className={`w-12 h-12 rounded-full flex items-center justify-center font-mono text-sm ${highlight ? "bg-[color:var(--sauge)]/25 text-[color:var(--sauge-light)] ring-1 ring-[color:var(--sauge)]/40" : "bg-[color:var(--bg-surface)] text-[color:var(--ivoire)]"}`}>
        {value != null ? value.toFixed(1) : "—"}
      </div>
      <div className="text-[10px] uppercase tracking-widest text-[color:var(--ivoire-subtle)] font-mono mt-1">{label}</div>
    </div>
  );
}
